import { validateColumnDrafts, type ColumnDraft } from './columnDrafts';

/**
 * The create-a-data-mapping sequence, lifted out of the wizard component so it
 * can be tested without rendering: table (when new) → mapping → optional first
 * sync, with rollback of a table this flow provisioned if the mapping fails.
 *
 * The caller supplies the four calls as plain functions, so tests drive the
 * failure paths (mapping rejects, rollback rejects) that are otherwise only
 * reachable against a live server.
 */

export interface CreateFlowDeps {
  createTable: (input: {
    name: string;
    description?: string;
    columns: Array<{ name: string; type: string; description: string }>;
    keyColumns: string[];
  }) => Promise<{ statefulTableId: string }>;
  createMapping: (input: {
    name: string;
    slug: string;
    statefulTableId: string;
    description: string | null;
    dataAssetId: string | null;
    dataAssetVersionId: string | null;
  }) => Promise<{ dataMappingId: string }>;
  deleteTable: (statefulTableId: string) => Promise<void>;
  syncFromSource: (dataMappingId: string) => Promise<void>;
}

export interface CreateFlowInput {
  tableMode: 'new' | 'existing';
  existingTableId: string;
  /** A table a previous attempt provisioned but never linked. */
  provisionedTableId: string | null;
  columns: ColumnDraft[];
  keyColumnIds: string[];
  tableName: string;
  tableDescription: string;
  mappingName: string;
  mappingSlug: string;
  mappingDescription: string;
  source: { dataAssetId: string; dataAssetVersionId: string } | null;
  syncNow: boolean;
}

export type CreateFlowResult =
  | { ok: true; dataMappingId: string }
  | {
      ok: false;
      /** Which message to show. `kind` lets the caller localize it. */
      kind: 'invalid-columns' | 'no-key-column' | 'request-failed';
      message?: string;
      error?: unknown;
      /**
       * A table this flow provisioned that is still out there — the mapping
       * failed and the rollback drop did too. The retry has to reuse it, since
       * it holds the name the retry would ask for.
       */
      provisionedTableId: string | null;
    };

export async function runCreateDataMappingFlow(
  input: CreateFlowInput,
  deps: CreateFlowDeps,
): Promise<CreateFlowResult> {
  const prepared = prepareTableStep(input);
  if (!prepared.ok) return prepared;

  let statefulTableId = prepared.statefulTableId;
  let provisionedTableId = input.provisionedTableId;

  if (prepared.create) {
    try {
      const created = await deps.createTable(prepared.create);
      statefulTableId = created.statefulTableId;
      provisionedTableId = created.statefulTableId;
    } catch (error) {
      return { ok: false, kind: 'request-failed', error, provisionedTableId };
    }
  }

  let dataMappingId: string;
  try {
    const mapping = await deps.createMapping({
      name: input.mappingName.trim(),
      slug: input.mappingSlug.trim(),
      statefulTableId,
      description: input.mappingDescription.trim() || null,
      dataAssetId: input.source?.dataAssetId ?? null,
      dataAssetVersionId: input.source?.dataAssetVersionId ?? null,
    });
    dataMappingId = mapping.dataMappingId;
  } catch (error) {
    const stillProvisioned = provisionedTableId
      ? await rollBackTable(provisionedTableId, deps)
      : null;
    return {
      ok: false,
      kind: 'request-failed',
      error,
      provisionedTableId: stillProvisioned,
    };
  }

  if (input.syncNow && input.source) {
    // A failed first sync is not a failed create — the mapping exists either
    // way, and its detail page shows why the frame failed.
    await deps.syncFromSource(dataMappingId).catch(() => undefined);
  }

  return { ok: true, dataMappingId };
}

/**
 * Resolves which table the mapping will point at: an existing one, one a prior
 * attempt already provisioned, or a create request to issue.
 */
function prepareTableStep(input: CreateFlowInput):
  | {
      ok: true;
      statefulTableId: string;
      create: Parameters<CreateFlowDeps['createTable']>[0] | null;
    }
  | (CreateFlowResult & { ok: false }) {
  if (input.tableMode === 'existing') {
    return { ok: true, statefulTableId: input.existingTableId, create: null };
  }

  const validation = validateColumnDrafts(input.columns, input.keyColumnIds);
  if (!validation.ok) {
    return {
      ok: false,
      kind: 'invalid-columns',
      message: validation.message,
      provisionedTableId: input.provisionedTableId,
    };
  }

  const keyColumns = input.keyColumnIds
    .map((id) => input.columns.find((column) => column.id === id))
    .filter((column): column is (typeof input.columns)[number] => !!column);
  if (keyColumns.length !== input.keyColumnIds.length) {
    // validateColumnDrafts already proved a key exists; this narrows the type.
    return {
      ok: false,
      kind: 'no-key-column',
      provisionedTableId: input.provisionedTableId,
    };
  }

  if (input.provisionedTableId) {
    // A previous attempt got as far as the table but failed after it.
    return {
      ok: true,
      statefulTableId: input.provisionedTableId,
      create: null,
    };
  }

  return {
    ok: true,
    statefulTableId: '',
    create: {
      name: input.tableName.trim() || input.mappingName.trim(),
      description: input.tableDescription.trim() || undefined,
      columns: validation.columns,
      keyColumns: keyColumns.map((column) => column.name.trim()),
    },
  };
}

/** Drops a provisioned table. Returns its id if the drop failed, else null. */
async function rollBackTable(
  statefulTableId: string,
  deps: CreateFlowDeps,
): Promise<string | null> {
  return deps
    .deleteTable(statefulTableId)
    .then(() => null)
    .catch(() => statefulTableId);
}
